/**
 * IconButton — generic pressable icon button.
 *
 * Ported from client-native/src/ui/atoms/pressable/IconButton.tsx (source of truth).
 * iOS semantics: hitSlop 10, press → opacity 0.6 (80ms in / 120ms out), default
 * icon color = grey #424242 → var(--color-grey) (Icon.tsx defaults color to
 * globalColors.grey = #424242), default icon size "medium" = 20px (see
 * atoms/visual/Icon.tsx). No background by default.
 *
 * Web port: web has no haptics, so the `haptics` prop is intentionally dropped.
 * Hit area is guaranteed ≥44px via padding (the visual icon stays at `size`).
 */
import { CSSProperties, FC, ReactNode, useState } from "react";
import type { LucideIcon } from "lucide-react";

export interface IconButtonProps {
  /** A lucide-react icon component, or any ReactNode to render inside. */
  icon: LucideIcon | ReactNode;
  onPress?: () => void;
  /** Icon color. Defaults to var(--color-grey) (iOS Icon default #424242). */
  color?: string;
  /** Visual icon size in px. Defaults to 20 (iOS "medium"). */
  size?: number;
  /** Accessible label for the button. */
  ariaLabel?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

// Minimum touch target (px) — ensures hit area >= 44px regardless of icon size.
const MIN_HIT_AREA = 44;

function isLucideIcon(icon: LucideIcon | ReactNode): icon is LucideIcon {
  // lucide icons are forwardRef render functions (typeof === 'object' or 'function').
  return typeof icon === "function" || typeof icon === "object" && icon !== null && "render" in (icon as object);
}

const IconButton: FC<IconButtonProps> = ({
  icon,
  onPress,
  color = "var(--color-grey)",
  size = 20,
  ariaLabel,
  disabled = false,
  style,
}) => {
  const [pressed, setPressed] = useState(false);

  const pad = Math.max(0, (MIN_HIT_AREA - size) / 2);

  const handleUp = () => setPressed(false);

  let content: ReactNode;
  if (isLucideIcon(icon)) {
    const Icon = icon as LucideIcon;
    content = <Icon size={size} color={color} aria-hidden />;
  } else {
    content = icon;
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onPress}
      onPointerDown={() => !disabled && setPressed(true)}
      onPointerUp={handleUp}
      onPointerLeave={handleUp}
      onPointerCancel={handleUp}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "none",
        border: "none",
        padding: pad,
        margin: 0,
        cursor: disabled ? "default" : "pointer",
        color,
        lineHeight: 0,
        // iOS Pressable stops responding when disabled with no visual change.
        opacity: pressed ? 0.6 : 1,
        transition: "opacity 80ms ease",
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        ...style,
      }}
    >
      {content}
    </button>
  );
};

export default IconButton;
